import { httpResource } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, effect, linkedSignal } from '@angular/core';

import { DEFAULT_TOTP_CONFIG } from '@arya/auth/domain';

import { NoxoIcon } from '@noxo/ui/icon';
import { NoxoListRow, NoxoListRowCaption, NoxoListRowTitle } from '@noxo/ui/list-row';
import { NoxoProgressBar } from '@noxo/ui/progress-bar';

export const DEMO_AUTHENTICATOR_URL = '/api/dev/demo/authenticator';

const TICK_MS = 1000;

export interface AuthenticatorCodes {
  readonly secondsLeft: number;
  readonly codes: readonly { readonly email: string; readonly code?: string }[];
}

@Component({
  selector: 'noxo-demo-authenticator',
  imports: [NoxoIcon, NoxoListRow, NoxoListRowCaption, NoxoListRowTitle, NoxoProgressBar],
  template: `
    @if (codes().length > 0) {
      <div class="countdown">
        <span>New codes in {{ secondsLeft() }} s</span>
        <progress
          noxo-progress-bar
          aria-label="Time until the codes change"
          [max]="period"
          [value]="secondsLeft()"></progress>
      </div>
    }
    @for (entry of codes(); track entry.email) {
      <div
        noxo-list-row
        tone="brand">
        <noxo-icon icon="mobile-screen-button" />
        <noxo-list-row-title class="code">{{
          entry.code ?? 'Secret sealed with another key, run Reset demo'
        }}</noxo-list-row-title>
        <noxo-list-row-caption>{{ entry.email }}</noxo-list-row-caption>
      </div>
    } @empty {
      <p class="hint">No account has an authenticator yet.</p>
    }
  `,
  styleUrls: ['./demo-overlay-section.scss', './demo-authenticator.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DemoAuthenticator {
  private readonly resource = httpResource<AuthenticatorCodes>(() => DEMO_AUTHENTICATOR_URL);
  private readonly rollsOverAt = computed(() =>
    this.resource.hasValue() ? Date.now() + this.resource.value().secondsLeft * TICK_MS : undefined
  );
  private readonly now = linkedSignal(() => {
    this.rollsOverAt();
    return Date.now();
  });

  protected readonly period = DEFAULT_TOTP_CONFIG.period;
  protected readonly codes = computed(() => (this.resource.hasValue() ? this.resource.value().codes : []));
  protected readonly secondsLeft = computed(() => {
    const rollsOverAt = this.rollsOverAt();
    return rollsOverAt === undefined ? 0 : Math.max(0, Math.ceil((rollsOverAt - this.now()) / TICK_MS));
  });

  constructor() {
    effect((onCleanup) => {
      if (this.rollsOverAt() === undefined || this.resource.isLoading()) return;
      if (this.secondsLeft() === 0) {
        this.resource.reload();
        return;
      }
      const timer = setTimeout(() => this.now.set(Date.now()), TICK_MS);
      onCleanup(() => clearTimeout(timer));
    });
  }
}
